import { QueryField, getQueryFieldVariables } from "@/utils/query"
import { BvToastOptions } from 'bootstrap-vue'
import html2canvas from "html2canvas"
import { isString, mapValues } from 'lodash'
import { Component, Mixins, Watch } from 'vue-property-decorator'
import { Route } from 'vue-router'
import { Getter, Mutation } from 'vuex-class'

import {
  Event,
  EventMixin
} from './widgets/mixins'

interface SetVariablePayload {
  path: string
  value: any
}

interface SetVariablesPayload {
  variables: Record<string, any>
}

interface ToastOptions extends BvToastOptions {
  message: string
}

type Operator = "eq" | "neq" | "gt" | "gte" | "lt" | "lte"

interface Condition {
  operator: Operator
  variable: string
  value: any
}

interface NavigateOptions {
  target: string
  conditions?: Condition[]
}

const evalCondition = (operator: Operator, a: any, b: any): boolean => {
  switch (operator) {

    case "eq": return a === b

    case "neq": return a !== b

    case "gt": return a > b

    case "gte": return a >= b

    case "lt": return a < b

    case "lte": return a <= b

    default:
      throw Error(`Unhandled operator: '${operator}'`)
  }
}


@Component
export default class EventManager extends Mixins(EventMixin) {
  @Getter('context/getVariable')
  getVariable!: (path: string) => any

  @Getter('form/getQueryFields')
  readonly queryFields!: QueryField[]

  @Event('set-variable')
  @Mutation('context/setVariable')
  setVariable!: (payload: SetVariablePayload) => void

  @Mutation('context/setVariables')
  setVariables!: (payload: SetVariablesPayload) => void

  @Event('set-variables')
  setVariablesEvent ({ variables }: SetVariablesPayload) {
    // TODO: Map variables as strings, requires system to be rewritten
    variables = mapValues(variables, path =>
      isString(path) ? this.getVariable(path) : path)
    this.setVariables({ variables })
  }

  get allEvents () {
    return this.$options.events
  }

  @Event('navigate')
  navigateEvent ({ target, conditions = [] }: NavigateOptions) {
    if (!conditions.every(({ operator, variable, value }) =>
      evalCondition(operator, this.getVariable(variable), value))) return

    this.$store.commit('form/setActiveWidgetId', target)
  }

  @Event('navigate-back')
  navigateBackEvent () {
    window.history.back()
  }

  @Event('reset')
  resetEvent () {
    this.$store.commit('form/resetState')
    // The form itself has not changed so do not clear the readonly variables
    this.$store.commit('context/resetState', { readonly: false })
    this.routeChanged(this.$route)
  }

  @Event('toast')
  toastEvent (options: ToastOptions) {
    const { message, ...rest } = options
    this.$bvToast.toast(message, rest)
  }

  @Event('lookupEmailContact')
  async lookupEmailContactEvent (options: any) {
    const { contact, form } = options

    // If the value is blank, do not lookup permissions
    if (!this.getVariable(contact)) {
      return
    }

    // Disable the passed form by id
    this.broadcastEvent('disable::form', form)

    try {
      // Lookup email permissions
      await this.$store.dispatch('event/lookupEmailContact', options)
    } catch {
      // pass
    } finally {
      // Enable the form again
      this.broadcastEvent('enable::form', form)
    }
  }

  @Event('lookupPhoneContact')
  async lookupPhoneContactEvent (options: any) {
    const { contact, form } = options

    // If the value is blank, do not lookup permissions
    if (!this.getVariable(contact)) {
      return
    }

    // Disable the passed form by id
    this.broadcastEvent('disable::form', form)

    try {
      // Lookup phone permissions
      await this.$store.dispatch('event/lookupPhoneContact', options)
    } catch {
      // pass
    } finally {
      // Enable the form again
      this.broadcastEvent('enable::form', form)
    }
  }

  @Event('lookupPhoneType')
  async lookupPhoneType (options: any) {
    const { contact, form } = options

    // If the value is blank, do not lookup permissions
    if (!this.getVariable(contact)) {
      return
    }

    // Disable the passed form by id
    this.broadcastEvent('disable::form', form)

    try {
      // Lookup phone permissions
      const { companyCode } = this.$route.params
      await this.$store.dispatch('event/lookupPhoneType', { ...options, companyCode })
    } catch {
      // pass
    } finally {
      // Enable the form again
      this.broadcastEvent('enable::form', form)
    }
  }

  @Event('screenshot')
  async screenshot (options: any) {
    const { target, path } = options

    // Get the document node for the widget
    const widget = document.getElementById(target)
    if (widget) {
      this.broadcastEvent('screenshot::capturing', target)
      const canvas = await html2canvas(widget)
      const dataUrl = canvas.toDataURL("image/png")
      this.broadcastEvent('screenshot::captured', target, { dataUrl })
      this.setVariable({ path, value: dataUrl })
    }
  }

  @Watch('$route', { immediate: true, deep: true })
  routeChanged (newValue: Route) {
    const fieldVariables = getQueryFieldVariables(this.queryFields, newValue.query)

    this.setVariables({
      variables: {
        ...fieldVariables,
        // Legacy group code param
        'form.groupCode': newValue.query.group || ''
      }
    })
  }

  // Renderless component
  render () {
    return null
  }
}
